import { Button, Checkbox, Link, makeStyles, Option, Radio, RadioGroup, SpinButton, SpinButtonOnChangeData, Text, tokens } from '@fluentui/react-components';
import { DismissRegular } from '@fluentui/react-icons';
import { clamp } from 'es-toolkit';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useState } from 'react';
import { useCommonStyles, useRubricPiiScan, useStores } from '../../../common/hooks';
import { awardExecutiveSummaryFileTemplateConfiguration } from '../../../configurations';
import { AwardLead, JudgingQuestion } from '../../../models';
import { UploadButton, ValidationMessageList } from '../../shared-components';
import { AssignDesignations } from '../../shared-components/assign-designations/AssignDesignations';
import AssignEligibilityRules from '../../shared-components/assign-eligibility-rules/AssignEligibilityRules';
import DocumentFile from '../../shared-components/DocumentFile';
import { FlexBox } from '../../shared-components/flex-box';
import { FormInput } from '../../shared-components/form/FormInput';
import { SelectOptions } from '../../shared-components/form/SelectOptions';
import { LeadAssignment } from '../../shared-components/lead-assignment/LeadAssignment';
import { RichTextEditor } from '../../shared-components/RichTextEditor';
import './AwardDetailForm.scss';

interface AwardDetailFormProps {
  onAddAwardTemplate(content: File): void;
  onDeleteAwardTemplate(): void;
  onAddAwardLead(emailAddress: string, isPrimaryLead: boolean): void;
  onDeleteAwardLead(awardLead: AwardLead): void;
  onAddJudgingQuestion(): void;
  onDeleteJudgingQuestion(judgingQuestion: JudgingQuestion): void;
}

const useStyles = makeStyles({
  judgingQuestionInputWrapper: {
    maxHeight: '25px'
  },
  piiWarning: {
    color: tokens.colorPaletteYellowForeground1,
  },
});

const AwardDetailFormImpl: React.FC<AwardDetailFormProps> = (props) => {
  const {
    onAddAwardTemplate,
    onDeleteAwardTemplate,
    onAddAwardLead,
    onDeleteAwardLead,
    onAddJudgingQuestion,
    onDeleteJudgingQuestion,
  } = props;

  const {
    awardStore,
    editAwardDetailPageUiStore,
  } = useStores();

  const styles = useStyles();
  const commonStyles = useCommonStyles();

  const { scan, piiWarning, clear } = useRubricPiiScan();

  useEffect(() => clear(), [editAwardDetailPageUiStore.editedAwardForStaff?.award.awardId, clear]);

  const [uploadFileErrorMessage, setUploadFileErrorMessage] = useState('');

  const uploadFile = (files: HTMLInputElement['files']) => {
    if (!files || !files[0]) {
      return;
    }

    if (!awardExecutiveSummaryFileTemplateConfiguration.supportedFileTypes.includes(files[0].type)) {
      setUploadFileErrorMessage('Select a Word, Excel, or PowerPoint file.');
      return;
    } else {
      setUploadFileErrorMessage('');
    }

    const maxFileSize = awardExecutiveSummaryFileTemplateConfiguration.maxFileSizeInMB * 1024 * 1024;
    if (files[0].size > maxFileSize) {
      setUploadFileErrorMessage('Upload a document that is 10 MB or smaller.');
      return;
    } else {
      setUploadFileErrorMessage('');
    }

    onAddAwardTemplate(files[0]);
  }

  const handleOnWeightChange = (value: string) => {
    const parsedValue = value !== '' ? parseInt(value) : undefined
    if (parsedValue && (parsedValue < 1 || parsedValue > 100)) return undefined;
    return parsedValue;
  };

  const handleMaxWinnersSpinButtonChange = (data: SpinButtonOnChangeData): void => {
    if (data.value === undefined || data.value === null) return;
    editAwardDetailPageUiStore.setEditedAwardField({
      key: 'maxWinners',
      value: clamp(data.value, 0, 255),
    });
  };

  const handleMaxFinalistsSpinButtonChange = (data: SpinButtonOnChangeData): void => {
    if (data.value === undefined || data.value === null) return;
    editAwardDetailPageUiStore.setEditedAwardField({
      key: 'maxFinalists',
      value: clamp(data.value, 0, 255),
    });
  };

  const renderJudgingQuestion = (judgingQuestion: JudgingQuestion, index: number): React.JSX.Element => {
    const validation = editAwardDetailPageUiStore.judgingQuestionValidations[index];

    return (
      <FlexBox
        isFullWidth
        direction='row'
        className='judging-question-item'
        key={judgingQuestion.questionId}
      >
        <FormInput
          className='question-input'
          label={`Judging question #${index + 1}`}
          onChange={(value) => judgingQuestion.question = value}
          validationMessage={
            editAwardDetailPageUiStore.isBeingValidated
              && validation.fieldErrors.question?.length
              ? (
                <ValidationMessageList
                  messages={validation.fieldErrors.question}
                />
              )
              : undefined
          }
          inputProps={{
            value: judgingQuestion.question,
            className: styles.judgingQuestionInputWrapper,
            contentAfter: (
              <Button
                appearance='subtle'
                onClick={() => onDeleteJudgingQuestion(judgingQuestion)}
                title='Remove'
                icon={<DismissRegular />}
              />
            ),
          }}
        />
        <FormInput
          className='weight-input'
          label={'Weight'}
          onChange={(value) => judgingQuestion.weight = handleOnWeightChange(value)}
          validationMessage={
            editAwardDetailPageUiStore.isBeingValidated
              && validation.fieldErrors.weight?.length
              ? (
                <ValidationMessageList
                  messages={validation.fieldErrors.weight}
                />
              )
              : undefined
          }
          inputProps={{
            value: judgingQuestion.weight ? judgingQuestion.weight.toString() : '',
            className: styles.judgingQuestionInputWrapper,
            contentAfter: '%',
            type: 'number',
            min: 1,
            max: 100
          }}
        />
      </FlexBox>
    );
  }

  return (
    <>
      {
        editAwardDetailPageUiStore.editedAwardForStaff && (
          <FlexBox
            direction='column'
            aligns='start'
            className='award-detail-form'
          >
            <RichTextEditor
              label='Description'
              key={`summary${editAwardDetailPageUiStore.editedAwardForStaff.award.awardId}`}
              value={editAwardDetailPageUiStore.editedAwardForStaff.award.summary}
              onChange={(content) => editAwardDetailPageUiStore.setEditedAwardField({ key: 'summary', value: content })}
            />
            <RichTextEditor
              label='Nomination requirements'
              key={`detail${editAwardDetailPageUiStore.editedAwardForStaff.award.awardId}`}
              value={editAwardDetailPageUiStore.editedAwardForStaff.award.detail}
              onChange={(content) => editAwardDetailPageUiStore.setEditedAwardField({ key: 'detail', value: content })}
            />
            <div className='award-detail-form__agent-scrubbing'>
              <FormInput
                label='Agent scrubbing standards'
                descriptionContent={<Text size={200}>Enter general scrubbing standards - do not include names or details of specific nominees.</Text>}
                onChange={(value) => editAwardDetailPageUiStore.setEditedAwardField({ key: 'defaultScrubbingRubric', value: value })}
                textAreaProps={{
                  value: editAwardDetailPageUiStore.editedAwardForStaff.award.defaultScrubbingRubric ?? '',
                  onBlur: (event) => scan(event.currentTarget.value),
                  resize: 'vertical',
                }}
              />
              {piiWarning && <Text block className={styles.piiWarning}>{piiWarning}</Text>}
            </div>
            <FlexBox
              isFullWidth
              aligns='start'
              direction='column'
              className='award-detail-form__judging-questions'
            >
              {editAwardDetailPageUiStore.editedAwardForStaff.judgingQuestions.map(renderJudgingQuestion)}
              <Link
                as='button'
                onClick={onAddJudgingQuestion}
                className={commonStyles.buttonAsLink}
              >
                + Add new judging question
              </Link>
            </FlexBox>
            <div>
              <Text
                id='judge-capacity-label'
                block
                weight='semibold'
              >
                Judge Capacity
              </Text>
              <SelectOptions
                tooltipText='Select the maximum number of nominations 1 judge can evaluate.'
                onOptionSelect={(data) => {
                  if (!data.optionValue) return;
                  editAwardDetailPageUiStore.setEditedAwardField({
                    key: 'judgeCapacity',
                    value: Number(data.optionValue),
                  });
                }}
                dropdownProps={{
                  'aria-labelledby': 'judge-capacity-label',
                  value: editAwardDetailPageUiStore.selectedJudgeCapacityValue,
                  selectedOptions: editAwardDetailPageUiStore.selectedJudgeCapacityOptions,
                  disabled: !editAwardDetailPageUiStore.judgeCapacityOptions.length,
                }}
              >
                {
                  editAwardDetailPageUiStore.judgeCapacityOptions.map((option) => (
                    <Option
                      key={option.value}
                      value={String(option.value)}
                    >
                      {option.text}
                    </Option>
                  ))
                }
              </SelectOptions>
            </div>
            {
              !editAwardDetailPageUiStore.editedAwardForStaff.award.isCountryAward && (
                <>
                  <div>
                    <Text
                      id='max-winners-label'
                      block
                      weight='semibold'
                    >
                      Max Winners
                    </Text>
                    <SpinButton
                      value={editAwardDetailPageUiStore.editedAwardForStaff.award.maxWinners}
                      onChange={(_, data) => handleMaxWinnersSpinButtonChange(data)}
                      aria-labelledby='max-winners-label'
                    />
                  </div>
                  <div>
                    <Text
                      id='max-finalists-label'
                      block
                      weight='semibold'
                    >
                      Max Finalists
                    </Text>
                    <SpinButton
                      value={editAwardDetailPageUiStore.editedAwardForStaff.award.maxFinalists}
                      onChange={(_, data) => handleMaxFinalistsSpinButtonChange(data)}
                      aria-labelledby='max-finalists-label'
                    />
                  </div>
                </>
              )
            }
            <FlexBox
              isFullWidth
              content='space-between'
              className='award-detail-form__columns'
            >
              <div>
                <Text block weight='semibold'>Customer references</Text>
                <Checkbox
                  checked={editAwardDetailPageUiStore.editedAwardForStaff.award.requireCustomerReference}
                  label='This award allows customer references'
                  onChange={(_, data) => editAwardDetailPageUiStore.setEditedAwardField({ key: 'requireCustomerReference', value: (data.checked === true) })}
                />
              </div>
              <div>
                <Text block weight='semibold'>Joint nominations</Text>
                <Checkbox
                  checked={editAwardDetailPageUiStore.editedAwardForStaff.award.allowJointNomination}
                  label='This award can have joint nominees'
                  onChange={(_, data) => editAwardDetailPageUiStore.setEditedAwardField({ key: 'allowJointNomination', value: (data.checked === true) })}
                />
              </div>
            </FlexBox>
            <FlexBox
              isFullWidth
              content='space-between'
              className='award-detail-form__columns'
            >
              <LeadAssignment
                title='Primary award lead'
                isPrimaryLead={true}
                leads={editAwardDetailPageUiStore.editedAwardForStaff.awardLeads}
                allowMultipleEmailAddresses={false}
                onAddLead={emailAddress => onAddAwardLead(emailAddress, true)}
                onDeleteLead={lead => onDeleteAwardLead(lead as AwardLead)}
              />
              <LeadAssignment
                title='Award assistants'
                isPrimaryLead={false}
                leads={editAwardDetailPageUiStore.editedAwardForStaff.awardLeads}
                allowMultipleEmailAddresses={false}
                onAddLead={emailAddress => onAddAwardLead(emailAddress, false)}
                onDeleteLead={lead => onDeleteAwardLead(lead as AwardLead)}
              />
            </FlexBox>
            <FlexBox
              isFullWidth
              content='space-between'
              className='award-detail-form__columns'
            >
              <FlexBox
                isFullWidth
                direction='column'
                className='columns-eligibility'
              >
                <Text weight='semibold'>Eligibility Criteria</Text>
                <AssignDesignations
                  label='Designations'
                  allDesignationCheckedText='Any Solutions Partner Designation'
                  designations={editAwardDetailPageUiStore.designationOptions}
                  selectedDesignationCodes={editAwardDetailPageUiStore.editedAwardForStaff.award.eligibilityRuleCodes}
                  onDesignationsChange={(updatedDesignations) => editAwardDetailPageUiStore.handleOnDesignationsChange(updatedDesignations)}
                />
                <AssignDesignations
                  label='Specializations'
                  allDesignationCheckedText='Any Specialization Designation'
                  designations={editAwardDetailPageUiStore.specializationOptions}
                  selectedDesignationCodes={editAwardDetailPageUiStore.editedAwardForStaff.award.eligibilityRuleCodes}
                  onDesignationsChange={(updatedDesignations) => editAwardDetailPageUiStore.handleOnSpecializationDesignationsChange(updatedDesignations)}
                />
                <AssignEligibilityRules
                  eligibilityRules={awardStore.getEligibilityRules('Category')}
                  selectedEligibilityRuleCodes={editAwardDetailPageUiStore.editedAwardForStaff.award.eligibilityRuleCodes}
                />
              </FlexBox>
              <FlexBox
                isFullWidth
                direction='column'
                className='columns-format'
              >
                <Text weight='semibold'>Award format</Text>
                <RadioGroup>
                  <Radio
                    label='Rich text editor'
                    checked={editAwardDetailPageUiStore.editedAwardForStaff.award.requireWrittenNomination}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => editAwardDetailPageUiStore.setEditedAwardField({ key: 'requireWrittenNomination', value: event.target.checked })}
                  />
                  <Radio
                    label='Template'
                    checked={!editAwardDetailPageUiStore.editedAwardForStaff.award.requireWrittenNomination}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => editAwardDetailPageUiStore.setEditedAwardField({ key: 'requireWrittenNomination', value: !event.target.checked })}
                  />
                </RadioGroup>
                {
                  !editAwardDetailPageUiStore.editedAwardForStaff.award.requireWrittenNomination &&
                  <UploadButton
                    onUpload={uploadFile}
                    validationMessage={uploadFileErrorMessage}
                    buttonProps={{
                      children: 'Upload template',
                      disabled: editAwardDetailPageUiStore.editedAwardForStaff.awardExecutiveSummaryFileTemplate !== undefined,
                    }}
                  >
                    {
                      editAwardDetailPageUiStore.editedAwardForStaff.awardExecutiveSummaryFileTemplate?.fileName &&
                      <DocumentFile
                        filename={editAwardDetailPageUiStore.editedAwardForStaff.awardExecutiveSummaryFileTemplate.fileName}
                        onDeleteFile={onDeleteAwardTemplate}
                      />
                    }
                  </UploadButton>
                }
              </FlexBox>
            </FlexBox>
          </FlexBox >
        )
      }
    </>
  );
}

export const AwardDetailForm = observer(AwardDetailFormImpl);
